/* AppHome.tsx generated by @compiled/babel-plugin v3.0.2 */
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._2mHwu81Y8M%7Bgap%3Avar(--ds-space-050%2C4px)%7D%0A._3iDTPbQ4SZ%7Bdisplay%3Aflex%7D%0A._0hPCNLDIqT%7Balign-items%3Acenter%7D");


import { Fragment, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { AppLogo, CustomLogo } from '@atlaskit/navigation-system/top-nav-items';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items/custom-title';
import type { AppHomeProps, LogoComponent } from '../../common/types';
import type { AppHome$key } from './__generated__/AppHome.graphql';
const styles = {
  root: "_2mHwu81Y8M _3iDTPbQ4SZ _0hPCNLDIqT"
};

/**
 * __App Home__
 *
 * Renders the app tile (icon/logo) and title shown at the start of the top
 * navigation, using the ADS `AppLogo` / `CustomLogo` / `CustomTitle`
 * top-nav-item primitives.
 *
 * ## Unit title vs. custom title
 *
 * These render in two different places, matching their different ADS
 * primitives:
 *
 * - The unit title (loaded internally via the `AppHome` GraphQL fragment —
 *   see `queryRef`) renders as `AppLogo`'s `secondaryName` — a
 *   second line directly under the app name (see ASF-1733). It's only shown
 *   when there is no `customLogo` (which replaces `AppLogo` entirely, and
 *   has no equivalent slot) and no `customTitle` (see below).
 * - `customTitle` (e.g. a site description) renders as a separate
 *   `CustomTitle` item alongside the logo, in both the `AppLogo` and
 *   `CustomLogo` cases, and always wins over the unit title when both are
 *   present.
 *
 * Unlike the rest of App Home's props, the unit title is NOT a plain prop —
 * it's loaded internally via `useFragment` from `queryRef`, rather
 * than being derived and passed as a string by each consumer (see
 * NAVX-3388/NAVX-5411). Inside `GlobalTopNavigation` that reference is already
 * loaded and reachable as `context.queryRef` on every slot's props, so a slot
 * passes it through rather than fetching it again.
 *
 * App Home does not fire its own analytics — it's a purely presentational
 * component. Consumers wanting click analytics should wire it into the
 * `onClick` prop themselves (e.g. Jira fires its own event there when it
 * adopts this component via `AppHomeSlot`).
 */
export function AppHome(props: Partial<AppHomeProps>): JSX.Element {
  const {
    appName = '',
    label,
    href = '',
    icon,
    customLogo,
    customIcon,
    customTitle,
    queryRef,
    onClick,
    testId
  } = props;

  // No local @argumentDefinitions: `appSwitcherProductAri`/
  // `skipAppSwitcherUnitProfile` are the query-level variables Jira already
  // declares on its root operation for this exact field (see NAVX-5411's
  // Nav4AppSwitcher usage) -- they're in scope for every fragment spread
  // into that same operation, so no local argument threading is needed.
  const unitProfileData = useFragment<AppHome$key>(graphql`
			fragment AppHome on Query {
				appSwitcher_unitProfile(productAri: $appSwitcherProductAri)
					@skip(if: $skipAppSwitcherUnitProfile) {
					__typename
					... on AppSwitcherUnitProfile {
						unitName
					}
				}
			}
		`, queryRef ?? null);
  const unitTitle = unitProfileData?.appSwitcher_unitProfile?.__typename === 'AppSwitcherUnitProfile' ? unitProfileData.appSwitcher_unitProfile.unitName : undefined;
  const accessibleLabel = label ?? appName;
  // The unit title only has somewhere to render (AppLogo's `secondaryName`)
  // when there's no custom logo, and only when it isn't already occupied by
  // a custom title.
  const secondaryName = !customLogo && !customTitle ? unitTitle : undefined;

  // Memoised so that, as long as the prop values themselves are referentially
  // stable (the common case — string URLs, or a component declared at module
  // scope), `AppLogo`/`CustomLogo` receive the same `icon`/`logo` component
  // reference across renders. Without this, `toLogoComponent` would return a
  // brand new function type on every render, which React would treat as a
  // different component type — unmounting and remounting the logo/icon
  // subtree (and losing any DOM state) on every single render.
  const resolvedIcon = useMemo(() => icon ? toLogoComponent(icon, accessibleLabel) : EmptyLogo, [icon, accessibleLabel]);
  const resolvedCustomLogo = useMemo(() => customLogo ? toLogoComponent(customLogo, accessibleLabel) : undefined, [customLogo, accessibleLabel]);
  const resolvedCustomIcon = useMemo(() => customLogo ? toLogoComponent(customIcon || icon || customLogo, accessibleLabel) : undefined, [customIcon, icon, customLogo, accessibleLabel]);
  return <span data-testid={testId} className={ax([styles.root])}>
			{resolvedCustomLogo ? <CustomLogo label={accessibleLabel} href={href} logo={resolvedCustomLogo} icon={resolvedCustomIcon ?? resolvedCustomLogo} onClick={onClick} /> : <AppLogo name={appName} secondaryName={secondaryName} label={accessibleLabel} href={href} icon={resolvedIcon} onClick={onClick} />}
			{customTitle ? <CustomTitle testId={testId && `${testId}--title`}>{customTitle}</CustomTitle> : null}
		</span>;
}

/**
 * Wraps a plain image URL into the `(props: LogoProps) => JSX.Element` render
 * shape that ADS's `AppLogo` / `CustomLogo` expect for their `icon`/`logo`
 * props, so simple apps can pass a URL instead of building their own
 * component.
 */
function createUrlLogo(src: string, alt: string): LogoComponent {
  return function UrlLogo() {
    return <img src={src} alt={alt} />;
  };
}

/**
 * Normalises an `icon`/`customLogo`/`customIcon` prop (which may be a plain
 * URL or an existing `@atlaskit/logo`-shaped component) into the component
 * shape ADS's top-nav-item primitives require.
 */
function toLogoComponent(iconOrLogo: string | LogoComponent, alt: string): LogoComponent {
  return typeof iconOrLogo === 'string' ? createUrlLogo(iconOrLogo, alt) : iconOrLogo;
}

/**
 * Renders nothing. Used as the icon when no `icon` is supplied at all, so the
 * app name is still shown as text via `AppLogo` rather than App Home
 * rendering empty.
 */
function EmptyLogo(): JSX.Element {
  return <Fragment />;
}